// src/pages/LoginPage.tsx (or your main auth page file)
import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../store/ContextStore';
import { useChatHistory } from '../store/ChatHistoryContext';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import LoginForm from '../components/LoginForm';
import CreateUserForm from '../components/CreateUserForm';
import localStorageService from '../services/localStorageService';
import AuthService from '../services/authService';
import RecordbookUploadNoticeModal from '../components/modals/RecordbookUploadNoticeModal';
import { parseRegisterError, parseRegisterOnboardingError } from '../utils/registerError';
import { fetchAllTeams } from '../api/authTeams';

interface LoginErrorState {
    Username?: string;
    Password?: string;
    General?: string;
}

interface CreateUserErrorState {
    UserName?: string;
    Email?: string;
    fullName?: string;
    TeamId?: string;
    designation?: string;
    General?: string;
    onboardingContactEmail?: string;
}

interface TeamPayload {
    code: number | null;
    name: string;
    gTeamId?: number;
    tidyName?: string;
    imgUrl?: string;
    colorCode?: string;
}

// Define Sport interface here instead of importing it
interface Sport {
    name: string;
    code: string;
    gLeagueId?: string | number;
}

export default function CreateUser() {
    // Always force signup view when accessing this component directly
    const [isLoginView, setIsLoginView] = useState(false);
    const [isPending, setIsPending] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    
    // Force signup view when accessed directly via URL
    useEffect(() => {
        if (location.pathname === '/signup') {
            setIsLoginView(false);
        }
    }, [location.pathname]);

    useEffect(() => {
        void fetchAllTeams().catch(() => {});
    }, []);

    const [loginUsername, setLoginUsername] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [loginError, setLoginError] = useState<LoginErrorState>({});
    const [showLoginPassword, setShowLoginPassword] = useState(false);

    const [createUsername, setCreateUsername] = useState('');
    const [createEmail, setCreateEmail] = useState('');

    const [isRecordbookModalOpen, setIsRecordbookModalOpen] = useState(false);
    const [recordbookTeamName, setRecordbookTeamName] = useState<string | undefined>(undefined);
    const [recordbookMissing, setRecordbookMissing] = useState<Record<string, unknown> | null>(null);


    const [createError, setCreateError] = useState<CreateUserErrorState>({});
    const [showCreatePassword, setShowCreatePassword] = useState(false);
    const [showCreateConfirmPassword, setShowCreateConfirmPassword] = useState(false);

    const { setUser, setIsAuthenticated, setSelectedSport, setTeam: setAuthTeam } = useContext(AuthContext);
    const { clearHistory } = useChatHistory();
    const authService = new AuthService();

    const handleLogin = async () => {
        clearHistory();
        localStorage.clear();
        setLoginError({});
        setIsPending(true);

        if (!loginUsername.trim() || !loginPassword.trim()) {
            setLoginError({ General: "Username and Password are required." });
            if (!loginUsername.trim()) setLoginError(prev => ({ ...prev, Username: "Username required" }));
            if (!loginPassword.trim()) setLoginError(prev => ({ ...prev, Password: "Password required" }));
            setIsPending(false);
            return;
        }

        const payload = new URLSearchParams();
        payload.append('username', loginUsername);
        payload.append('password', loginPassword);

        const response = await authService.UserLogin(payload);
        setIsPending(false);

        if (response && response.status === 200) {
            const userData = normalizeUserFromSubscription(response.data);
            localStorageService.addUserInfo(userData);
            const sportCodes: string[] = Array.isArray(userData?.sports) ? userData.sports.map((s: any) => s?.code).filter(Boolean) : [];
            const sportCode = sportCodes.includes('MFB') ? 'MFB' : (sportCodes[0] || null);
            if (sportCode) localStorageService.setSelectedSport(sportCode);

            setUser(userData);
            setIsAuthenticated(true);
            if (sportCode) setSelectedSport(sportCode);
            setAuthTeam(userData.team || null);
            navigate("/dashboard");
        } else {
            const errorMsg = response?.data?.message || response?.data?.detail || response?.message || "Invalid username or password.";
            setLoginError({ General: errorMsg });
        }
    };


    const handleCreateUser = async (payload: any) => {
        setCreateError(prev => ({ ...prev, General: "" }));
        setIsPending(true);
        // console.log("payload", payload);
        const response = await authService.UserCreate(payload);
        setIsPending(false);

        if (response && (response.status === 201 || response.status === 200)) {
            const recordbookUploadRequired = Boolean(response?.data?.recordbook_upload_required);
            if (recordbookUploadRequired) {
                setRecordbookTeamName(response?.data?.team_name);
                setRecordbookMissing(response?.data?.recordbook_missing ?? null);
                setIsRecordbookModalOpen(true);
                return;
            }

            alert(response.data?.message || "Request sent successfully! Please check your mail for details.");
            setCreateUsername('');
            setCreateEmail('');
            navigate('/login');
        } else {
            const onboardingError = parseRegisterOnboardingError(response);
            if (onboardingError) {
                setCreateError({
                    General: onboardingError.message,
                    onboardingContactEmail: onboardingError.contact_email,
                });
            } else {
                setCreateError({ General: parseRegisterError(response) });
            }
        }
    };

    const switchToCreateUserView = () => {
        setIsLoginView(false);
        setLoginError({});
    };
    
    const switchToLoginView = () => {
        // If we're on the dedicated signup page, navigate to the login page
        if (location.pathname === '/signup') {
            navigate('/login');
        } else {
            // Otherwise toggle the view within the current page
            setIsLoginView(true);
            setCreateError({});
        }
    };

    return (
        <div className="w-full min-h-screen flex flex-col justify-center items-center box-border bg-gradient-to-br from-gray-100 to-cyan-50 py-12 px-4">
            <RecordbookUploadNoticeModal
                isOpen={isRecordbookModalOpen}
                teamName={recordbookTeamName}
                recordbookMissing={recordbookMissing}
                onAcknowledge={() => {
                    setIsRecordbookModalOpen(false);
                    setCreateUsername('');
                    setCreateEmail('');
                    navigate('/login');
                }}
            />
            <CreateUserForm
                handleCreateUser={handleCreateUser}
                username={createUsername}
                setUsername={setCreateUsername}
                email={createEmail}
                setEmail={setCreateEmail}
                error={createError}
                setError={setCreateError}
                isPending={isPending}
                switchToLogin={switchToLoginView}
            />
        </div>
    );
}
